/**
 * SessionHistoryPanel — Phase B addition.
 *
 * Persistent, 90-day session history. Solves the "I see who's online but
 * the dashboard goes blank when they leave" problem.
 *
 * Three sections (top → bottom):
 *   1. DAU sparkline (last 30 days) with click-to-expand bar chart drill-down
 *   2. Recent Sessions table — last 50 sessions across all users with
 *      duration, last_seen, last_route, top actions, online/offline status
 *   3. Per-user session history modal — click any row to see that user's
 *      30-day history with totals and a per-session breakdown
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Clock, Wifi, WifiOff, AlertCircle, Crown,
  Smartphone, MapPin, Activity, RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";

// ── helpers ──────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function durationLabel(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// Lightweight inline SVG sparkline — no external chart lib needed.
function Sparkline({ data, width = 480, height = 64, accent = "#ec4899" }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.active_users || 0));
  const stepX = width / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = i * stepX;
    const y = height - ((d.active_users || 0) / max) * (height - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${points.join(" L ")}`;
  const area = `${path} L ${width.toFixed(1)},${height} L 0,${height} Z`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-16"
      preserveAspectRatio="none"
      data-testid="dau-sparkline"
    >
      <defs>
        <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#dauFill)" />
      <path d={path} fill="none" stroke={accent} strokeWidth="2" />
    </svg>
  );
}

// ── DAU drill-down (bar chart with hover-list) ───────────────────────
function DauBarChart({ data, onClose }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const max = Math.max(1, ...data.map((d) => d.active_users || 0));
  const hoverDay = hoverIdx != null ? data[hoverIdx] : null;
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-0.5 h-40 px-2 py-2 bg-muted/30 rounded-lg">
        {data.map((d, i) => {
          const h = ((d.active_users || 0) / max) * 100;
          const isHover = i === hoverIdx;
          return (
            <div
              key={d.day}
              className="relative flex-1 flex items-end cursor-pointer group"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => setHoverIdx(i)}
              data-testid={`dau-bar-${d.day}`}
            >
              <div
                className={`w-full rounded-t transition-colors ${
                  isHover ? "bg-pink-400" : "bg-pink-500/60 hover:bg-pink-500"
                }`}
                style={{ height: `${Math.max(2, h)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-2">
        <span>{data[0]?.day}</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
      {hoverDay && (
        <div className="p-3 bg-card border border-border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">{hoverDay.day}</span>
            <Badge>{hoverDay.active_users} active</Badge>
          </div>
          {hoverDay.users.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {hoverDay.users.slice(0, 30).map((u) => (
                <span key={u.user_id} className="text-[11px] px-2 py-0.5 rounded bg-muted">
                  {u.user_name || "Unknown"}
                </span>
              ))}
              {hoverDay.users.length > 30 && (
                <span className="text-[11px] text-muted-foreground self-center">
                  +{hoverDay.users.length - 30} more
                </span>
              )}
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground italic">No active users</p>
          )}
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={onClose} className="w-full">
        <X className="w-3 h-3 mr-1" /> Hide drill-down
      </Button>
    </div>
  );
}

// ── per-user history modal ───────────────────────────────────────────
function UserHistoryModal({ userId, userName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}/sessions?days=30`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Could not load history"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="user-history-modal">
        <DialogHeader>
          <DialogTitle>{userName || "User"} — last 30 days</DialogTitle>
          <DialogDescription>
            All sessions ordered newest first. Includes online + offline.
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="text-center py-6 text-sm text-muted-foreground">Loading history…</div>
        )}
        {!loading && data && (
          <>
            <div className="grid grid-cols-4 gap-2 my-2">
              <KpiSmall label="Sessions" value={data.count} />
              <KpiSmall label="Total time" value={durationLabel(data.total_session_seconds)} />
              <KpiSmall label="Events" value={data.total_events} />
              <KpiSmall
                label="Crashes"
                value={data.crash_count}
                highlight={data.crash_count > 0 ? "danger" : null}
              />
            </div>
            {data.sessions.length === 0 && (
              <p className="text-center py-4 text-sm text-muted-foreground italic">
                No sessions in this window.
              </p>
            )}
            <div className="space-y-2">
              {data.sessions.map((s) => <SessionRow key={s.id} s={s} />)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiSmall({ label, value, highlight }) {
  const colour = highlight === "danger" ? "text-red-400" : "text-foreground";
  return (
    <div className="bg-muted/40 rounded p-2">
      <p className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</p>
      <p className={`text-lg font-semibold ${colour}`}>{value}</p>
    </div>
  );
}

function SessionRow({ s, onClickUser }) {
  const isOnline = s.status === "online";
  return (
    <div
      className="p-2.5 rounded border border-border bg-card hover:border-border/80 transition-colors"
      data-testid={`session-row-${s.session_id}`}
    >
      {/* Top row — always full-width so the name never gets squashed to one
          character on narrow screens. Mobile admins were seeing "P." instead
          of "Patricia" because flex-wrap let the meta row steal width. */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? "bg-emerald-400" : "bg-zinc-500"}`} />
        {onClickUser ? (
          <button
            type="button"
            onClick={() => onClickUser(s.user_id, s.user_name)}
            className="font-medium text-sm hover:underline truncate text-left flex-1 min-w-0"
            data-testid={`session-user-${s.session_id}`}
          >
            {s.user_name || "Unknown"}
          </button>
        ) : (
          <span className="font-medium text-sm truncate flex-1 min-w-0">{s.user_name || "Unknown"}</span>
        )}
        {s.premium_at_start && <Crown className="w-3 h-3 text-amber-400 shrink-0" />}
        {s.crashed && (
          <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30 shrink-0">
            <AlertCircle className="w-2.5 h-2.5 mr-1" />Crashed
          </Badge>
        )}
      </div>
      {/* Meta row — second line, wraps freely without competing for name width */}
      <div className="mt-1 pl-3.5 text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center" title="Duration">
          <Clock className="w-3 h-3 mr-1" /> {durationLabel(s.duration_seconds)}
        </span>
        <span className="inline-flex items-center" title="Last seen">
          {isOnline ? <Wifi className="w-3 h-3 mr-1 text-emerald-400" /> : <WifiOff className="w-3 h-3 mr-1" />}
          {relativeTime(s.last_seen)}
        </span>
        <span className="inline-flex items-center" title="Last route">
          <MapPin className="w-3 h-3 mr-1" /> {s.last_route || "/"}
        </span>
        {s.platform && (
          <span className="inline-flex items-center" title="Platform">
            <Smartphone className="w-3 h-3 mr-1" /> {s.platform}
          </span>
        )}
        <span className="inline-flex items-center" title="Events fired">
          <Activity className="w-3 h-3 mr-1" /> {s.event_count || 0}
        </span>
      </div>
      {s.top_actions && s.top_actions.length > 0 && (
        <div className="mt-1.5 pl-3.5 flex flex-wrap gap-1">
          {s.top_actions.map((ta) => (
            <span
              key={ta.action}
              className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
            >
              {ta.action} ×{ta.count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── main panel ───────────────────────────────────────────────────────
export default function SessionHistoryPanel({ embedded = true }) {
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [includeActive, setIncludeActive] = useState(true);
  const [dau, setDau] = useState([]);
  const [dauOpen, setDauOpen] = useState(false);
  const [drillUser, setDrillUser] = useState(null);

  const reloadRecent = useCallback(async (active = includeActive) => {
    setRecentLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/recent-sessions`, {
        params: { limit: 50, include_active: active },
      });
      setRecent(data.sessions || []);
    } catch (e) {
      toast.error("Could not load sessions");
    } finally {
      setRecentLoading(false);
    }
  }, [includeActive]);

  const reloadDau = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dau-stats`, { params: { days: 30 } });
      setDau(data.series || []);
    } catch (e) { /* swallow — DAU is non-critical */ }
  }, []);

  useEffect(() => { reloadRecent(); reloadDau(); }, [reloadRecent, reloadDau]);

  const onlineCount = useMemo(() => recent.filter((s) => s.status === "online").length, [recent]);

  return (
    <div className="space-y-4" data-testid="session-history-panel">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Daily active users</CardTitle>
            <p className="text-[11px] text-muted-foreground">Last 30 days</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDauOpen((v) => !v)}
            data-testid="dau-toggle"
          >
            {dauOpen ? "Hide" : "Drill down"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {!dauOpen && <Sparkline data={dau} />}
          {dauOpen && <DauBarChart data={dau} onClose={() => setDauOpen(false)} />}
          {dau.length > 0 && !dauOpen && (
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>{dau[0]?.day}</span>
              <span>{dau[dau.length - 1]?.day}: {dau[dau.length - 1]?.active_users} active</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Recent sessions</CardTitle>
            <p className="text-[11px] text-muted-foreground">
              {recent.length} total · {onlineCount} online · 90-day retention · click name for history
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={includeActive}
                onChange={(e) => { setIncludeActive(e.target.checked); reloadRecent(e.target.checked); }}
                data-testid="include-active-toggle"
              />
              <span>Include online</span>
            </label>
            <Button size="sm" variant="ghost" onClick={() => reloadRecent()} data-testid="recent-sessions-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${recentLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {recentLoading && (
            <p className="text-center text-sm text-muted-foreground py-4">Loading…</p>
          )}
          {!recentLoading && recent.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-4 italic">
              No sessions yet — they'll appear here as users come online.
            </p>
          )}
          {recent.map((s) => (
            <SessionRow
              key={s.session_id}
              s={s}
              onClickUser={(userId, userName) => setDrillUser({ userId, userName })}
            />
          ))}
        </CardContent>
      </Card>

      <UserHistoryModal
        userId={drillUser?.userId}
        userName={drillUser?.userName}
        onClose={() => setDrillUser(null)}
      />
    </div>
  );
}
